import osmImage from "../img/basemaps/osm.png";
import ortoImage from "../img/basemaps/orto2.png";
import { PlaceSubtype } from "./enums";

export interface IConfig {
    defaults: IDefaults;
    basemaps: IBasemap[];
    objects: IService;
    municipalityObjects: IService;
    infra: IService;
    infraObjects: IService;
    routes: IServiceUrl;
    game: IServiceUrl;
    tracks: IServiceUrl;
    geoLocation?: ILocation;
    startingExtent: IExtent;
    images: IImages;
    search: ISearch;
}

export interface IDefaults {
    center: number[];
    clusterLayerId: string;
    selectedRouteLayerId: string;
    tracksLayerId: string;
    zoomToLevel: number;
    debugCoordinatesOnMap?: boolean; // true, to log out coordinates on map
}
export interface IBasemap {
    title: string;
    id: string;
    img: string;
    layers: IBasemapLayer[];
}
export interface IBasemapLayer {
    type: string;
    version?: string;
    spatialReference?: number;
    layers?: string[];
    imagetype?: string;
    url?: string;
    copyright?: string;
    visible: boolean;
}
export interface IService {
    title: string;
    id: string;
    url: string;
}
export interface IServiceUrl {
    url: string;
}
export interface IFilterOptions {
    title: string;
    value: number | string | boolean | number[];
}
export interface ILocation {
    geolocationOptions?: {
        maximumAge: number;
        timeout: number;
        enableHighAccuracy: boolean;
    };
}
export interface IExtent {
    xmax: number;
    xmin: number;
    ymax: number;
    ymin: number;
    spatialReference: {
        wkid: number;
    };
}
export interface IImages {
    url: string;
    layerBrowseName: string;
    fileTableName: string;
    routeLayerBrowseName: string;
    routeFileTableName: string;
    imageDetails: {
        height: number;
        thumbnailHeight: number;
    };
}

export interface ISearch {
    // options: any;
    // indexEng?: string[];
    fieldsLV: string[];
    fieldsENG: string[];
    searchFields?: string[];
}

export interface IFilterConfig {
    [fieldname: string]: IFilterOptions[];
}

const placeSubtypes: Record<PlaceSubtype, string> = {
    [PlaceSubtype.nr1]: "PLACESUBTYPE=1",
    [PlaceSubtype.AtputasVieta]: "Atpūtas vieta",
    [PlaceSubtype.DabasTaka]: "Dabas taka",
    [PlaceSubtype.AktivaAtputa_VeloTaka]: "Velotaka",
    [PlaceSubtype.DabasApskatesObjekts]: "Dabas apskates objekts",
    [PlaceSubtype.KulturvesturisksApskatesObjekts]: "Kultūrvēsturisks apskates objekts",
    [PlaceSubtype.MammadabaGalamerki]: "Mammadaba galamērķi",
    [PlaceSubtype.SkatuTornis]: "Skatu tornis",
    [PlaceSubtype.AktivaAtputa_Orientesanas]: "Orientēšanās",
    [PlaceSubtype.Udensturistiem]: "Ūdenstūristu atpūtas vieta",
    [PlaceSubtype.MarsrutiIedvesmai]: "Maršruti iedvesmai"
};

const placeSubtypesToShow: PlaceSubtype[] = [
    PlaceSubtype.nr1,
    PlaceSubtype.AtputasVieta,
    PlaceSubtype.DabasTaka,
    PlaceSubtype.AktivaAtputa_VeloTaka,
    PlaceSubtype.DabasApskatesObjekts,
    PlaceSubtype.KulturvesturisksApskatesObjekts,
    PlaceSubtype.MammadabaGalamerki,
    PlaceSubtype.SkatuTornis,
    PlaceSubtype.AktivaAtputa_Orientesanas,
    PlaceSubtype.Udensturistiem,
    PlaceSubtype.MarsrutiIedvesmai
];

export const tourismTypes: { url: string; title: Record<number, string> } = {
    url: "https://lvmkartes.lvm.lv/mammadaba/dist/img/icons/tourismType/",
    title: {
        1: "Atpūtas vieta",
        2: "Apskates objekts",
        3: "Pastaigu taka",
        4: "Ugunskura vieta",
        5: "Laivu noma",
        6: "Makšķerēšana",
        7: "Ūdens mājiņa",
        8: "Putnu vērošana",
        9: "Medības",
        10: "Pirts, sauna",
        11: "Naktsmītnes",
        12: "Telts vietas",
        13: "Konferenču zāle",
        14: "Bērnu rotaļlaukums",
        15: "Pludmale",
        16: "Sporta laukums",
        17: "Ēdināšana",
        18: "Piemērots ar kustību traucējumiem",
        19: "Piemērots velobraucējiem",
        20: "Nojume",
        21: "Zirgu izjādes",
        22: "Gida pakalpojumi",
        23: "Informācijas centrs",
        24: "Sēņošana",
        25: "Pie ūdeņiem",
        26: "Ogošana",
        27: "Pieejams ar bērnu ratiņiem",
        28: "Maksas objekts",
        29: "Kempera vietas",
        30: "Glempings",
        31: "Putnu vērošana",
        32: "Digitālā interaktivitāte",
        33: "WiFi",
        34: "Tualete",
        35: "Atļauts ar mājdzīvniekiem"
    }
};

export const tourismTypesToShow: string[] = [
    4,
    7,
    8,
    10,
    11,
    13,
    17,
    18,
    19,
    20,
    22,
    23,
    25,
    28,
    29,
    30,
    31,
    32,
    33
].map((i) => i.toString());

export const getFiltersConfig = (): IFilterConfig[] => {
    return [
        {
            PLACESUBTYPE: [
                ...placeSubtypesToShow.map((value) => ({ title: placeSubtypes[value], value })),
                { title: "Aktīvā atpūta", value: [4, 10] }
            ]
        },
        {
            VISITED: [{ title: "Emu bijis", value: 1 }]
        },
        {
            LIKE: [{ title: "Patīk", value: 1 }]
        },
        {
            ROUTE: [{ title: "Pievienots maršrutam", value: 1 }]
        },
        { TOURISMTYPESSTR: tourismTypesToShow.map((value) => ({ title: tourismTypes.title[value], value })) },
        {
            LVREGION: [
                { title: "Kurzeme", value: "1" },
                { title: "Latgale", value: "2" },
                { title: "Rīga un Pierīga", value: "3" },
                { title: "Vidzeme", value: "4" },
                { title: "Zemgale", value: "5" }
            ]
        },
        { nearby: [{ title: "Tuvākie", value: false }] }
    ];
};

const proxyUrl = "https://lvmkartes.lvm.lv/mammadaba/proxy/";

const mammaEndpoint = "mamma1b719989bb6144a28e0d804259abb01d";
// const mammaEndpoint = "test1b3d73a1ef3e4b6e9662e23a515832bc";

const imageEndpoint = "img0db4b879e5b49a811a4ad5bbd3d42"; // uz RPFileApi
// const imageEndpoint = "test_img0db4b879e5b49a811a4ad5bbd3d42"; // uz RPFileApiBeta

export const config: IConfig = {
    defaults: {
        center: [2695217.034668637, 7728877.262046031],
        clusterLayerId: "clusterLayer",
        selectedRouteLayerId: "selectedRouteLayer",
        tracksLayerId: "tracksLayer",
        zoomToLevel: 13,
        debugCoordinatesOnMap: false // set true, to display coordinates on map
    },
    basemaps: [
        {
            title: "Open Street Map",
            id: "osm",
            img: osmImage,
            layers: [
                {
                    type: "OSM",
                    copyright: "© OpenStreetMap veidotāji",
                    visible: true
                }
            ]
        },
        {
            title: "Ortofoto karte",
            id: "orto2",
            img: ortoImage,
            layers: [
                {
                    type: "WMS",
                    version: "1.3.0",
                    spatialReference: 3857,
                    layers: ["public:Orto_LKS"],
                    imagetype: "image/vnd.jpeg-png8",
                    url: "https://lvmgeoserver.lvm.lv/geoserver/ows",
                    visible: true
                }
            ]
        }
    ],
    objects: {
        title: "Tūrisma vietas LVM",
        id: "objects",
        url: proxyUrl + mammaEndpoint + "/MDvMainObjectsWGS_V2/FeatureServer"
    },
    municipalityObjects: {
        title: "Pašvaldību objekti",
        id: "municipalityData",
        url: proxyUrl + mammaEndpoint + "/MDvMainObjectsWGS_V2/FeatureServer"
    },
    infra: {
        title: "Tūrisma infrastruktūra",
        id: "infra",
        url: proxyUrl + mammaEndpoint + "/MDvMapInfraWGS_V2/MapServer/"
    },
    infraObjects: {
        title: "MammaDaba tūrisma vietas", // šie ir no Infra slāņa izvēlētie objekti, iekopēti 5. slānī
        id: "infraObjects",
        url: proxyUrl + mammaEndpoint + "/MDvMainObjectsWGS_V2/FeatureServer"
    },
    routes: {
        url: proxyUrl + mammaEndpoint + "/MDvReadyRoutesWGS/FeatureServer"
    },
    game: {
        url: proxyUrl + mammaEndpoint + "/MDvMDgameWGS/FeatureServer"
    },
    tracks: {
        url: proxyUrl + mammaEndpoint + "/MDvMapInfraWGS_V2/FeatureServer"
    },
    geoLocation: {
        geolocationOptions: {
            maximumAge: 0,
            timeout: 15000,
            enableHighAccuracy: true
        }
    },
    startingExtent: {
        xmax: 3041811.5406042933,
        xmin: 2408301.4501769207,
        ymax: 8151397.570389125,
        ymin: 7295302.853595379,
        spatialReference: {
            wkid: 3857
        }
    },
    images: {
        url: proxyUrl + imageEndpoint + "/api/",
        layerBrowseName: "PUBSDE.RecreationPlace",
        fileTableName: "PUBSDE.RecreationPlacePhoto",
        routeLayerBrowseName: "PUBSDE.MammadabaDefinedRoute",
        routeFileTableName: "PUBSDE.MammadabaDefinedRoutePhoto",
        imageDetails: {
            height: 600,
            thumbnailHeight: 150
        }
    },
    search: {
        // options: {
        //     tokenize: "forward", // Allows partial results - https://github.com/nextapps-de/flexsearch#tokenizer-prefix-search
        //     // do not use property 'encode', it does not work. Workaround is to use it into 'charset' property
        //     charset: "latin:simple", // 'charset': encoder value {"simple, "balance", etc } https://github.com/nextapps-de/flexsearch#encoders
        //     limit: 1000, // limits result count, default=100
        //     minlength: 1, // search string min length
        //     document: {
        //         id: "id",
        //         index: ["feature:values_:PLACENAME", "feature:values_:KEYWORDS", "feature:values_:ADRESS"]
        //     }
        // },
        // indexEng: ["feature:values_:PLACENAMEENG", "feature:values_:KEYWORDS", "feature:values_:ADRESS"], // fields for ENG search
        fieldsLV: ["PLACENAME", "KEYWORDS", "ADRESS"], // fields for LV search
        fieldsENG: ["PLACENAMEENG", "KEYWORDS", "ADRESS"] // fields for LV search
    }
};
